import { MS_PER_S } from '../utils/timeUnits'

export interface StationaryTrimOptions {
  movingSpeedMps: number
  sustainS: number
  marginS: number
}

export interface TrimmableSample {
  atMs: number
  speedMps: number
}

export const STATIONARY_TRIM_DEFAULTS: StationaryTrimOptions = {
  movingSpeedMps: 2,
  sustainS: 3,
  marginS: 30,
}

interface MovingSpan {
  startAtMs: number
  endAtMs: number
}

const isOrderedAndFinite = (points: readonly TrimmableSample[]): boolean =>
  points.every(
    (point, i) =>
      Number.isFinite(point.atMs) &&
      Number.isFinite(point.speedMps) &&
      (i === 0 || point.atMs >= points[i - 1].atMs)
  )

const sustainedMovingSpan = (
  points: readonly TrimmableSample[],
  options: StationaryTrimOptions
): MovingSpan | null => {
  const sustainMs = options.sustainS * MS_PER_S
  let span: MovingSpan | null = null
  let runStart: number | null = null
  let runEnd = 0
  const closeRun = () => {
    if (runStart !== null && runEnd - runStart >= sustainMs) {
      span = { startAtMs: span ? span.startAtMs : runStart, endAtMs: runEnd }
    }
    runStart = null
  }
  for (const point of points) {
    if (point.speedMps >= options.movingSpeedMps) {
      if (runStart === null) runStart = point.atMs
      runEnd = point.atMs
    } else {
      closeRun()
    }
  }
  closeRun()
  return span
}

export const trimStationaryEnds = <T>(
  samples: T[],
  read: (sample: T) => TrimmableSample,
  options: StationaryTrimOptions = STATIONARY_TRIM_DEFAULTS
): T[] => {
  const points = samples.map(read)
  if (!isOrderedAndFinite(points)) return samples
  const span = sustainedMovingSpan(points, options)
  if (span === null) return samples
  const marginMs = options.marginS * MS_PER_S
  const keepFrom = span.startAtMs - marginMs
  const keepTo = span.endAtMs + marginMs
  const first = points.findIndex((p) => p.atMs >= keepFrom)
  let last = points.length - 1
  while (last > first && points[last].atMs > keepTo) last--
  if (first === 0 && last === points.length - 1) return samples
  return samples.slice(first, last + 1)
}
